import { useMemo, useState, type ReactNode, useEffect } from "react";
import { useIsFetching, useQuery, useQueryClient } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";
import { BarChart3, RefreshCw } from "lucide-react";

import { Button } from "@/components/ui/Button";
import { Card, CardBody } from "@/components/ui/Card";
import { HoverHintContent, HoverHintTrigger, HoverHintWithGlyph, PageTitleWithHelp } from "@/components/ui/title-help";
import { ApiError, apiFetch } from "@/lib/api";
import { HUB_CACHE_CHANNEL } from "@/lib/hubCacheChannels";
import { useHubCacheStream } from "@/lib/useHubCacheStream";
import { ClampQcReportTab } from "@/pages/manufacturing/ClampQcReportTab";
import { DailyTaskReportTab, DAILY_TASK_REPORT_AUTO_REFRESH_MS } from "@/pages/manufacturing/DailyTaskReportTab";
import { ProductionCostReportTab } from "@/pages/manufacturing/ProductionCostReportTab";
import {
  MANUFACTURING_REPORTING_STATION_COLORS,
  MANUFACTURING_REPORTING_STATION_ACCENTS,
} from "@/pages/manufacturing/manufacturingReportingStationColors";
import { compareSgsProductionCalendarStationOrder } from "@/lib/sgsProductionCalendarStationOrder";
import { cn } from "@/lib/utils";
import { TimeTrackingStationHoverHint } from "./TimeTrackingStationHoverHint";
import type { ManufacturingScanBreakdownLine } from "./manufacturingReportingBreakdown";

interface ManufacturingDailyScanRow {
  date: string;
  counts: Record<string, number>;
  netSeconds: Record<string, number>;
  excludedNetSeconds?: Record<string, number>;
  breakdowns?: Record<string, ManufacturingScanBreakdownLine[]>;
}

interface ManufacturingStation {
  key: string;
  label: string;
  shortLabel: string;
}

interface ManufacturingDailyScanResponse {
  timeZone: string;
  dayCount: number;
  rollingDayCount?: number;
  reportWeekCount?: number;
  stations?: ManufacturingStation[];
  rollingRows?: ManufacturingDailyScanRow[];
  rows: ManufacturingDailyScanRow[];
  cached_at?: string;
  stale?: boolean;
  revalidating?: boolean;
  next_refresh_after?: string | null;
}

interface WeeklyScanRollup {
  label: string;
  startDate: string;
  endDate: string;
  days: ManufacturingReportDay[];
  counts: Record<string, number>;
  netSeconds: Record<string, number>;
}

interface ManufacturingReportDay {
  key: string;
  title: string;
  subtitle: string;
  rows: ManufacturingDailyScanRow[];
  counts: Record<string, number>;
  netSeconds: Record<string, number>;
}

interface UnattributedLaborRow {
  scanId: number;
  scannedAt: string;
  stationKey: string;
  stationPage: string;
  userId: string | null;
  userEmail: string | null;
  operatorName: string | null;
  operatorAvatarUrl: string | null;
  deviceSerial: string;
  serialFciId: number | null;
  serialFinishedSku: string | null;
  batchItemId: number | null;
  batchItemSku: string | null;
  batchId: number | null;
  allocatedSeconds: number;
  resolutionPath: string;
}

interface UnattributedLaborByBatchItemRow {
  groupKey: string;
  batchItemId: number | null;
  sku: string | null;
  skuLabel: string;
  batchItemLabel: string | null;
  batchId: number | null;
  netSeconds: number;
  rowCount: number;
}

interface UnattributedLaborByBatchRow {
  batchId: number | null;
  netSeconds: number;
  rowCount: number;
  sampleSku: string | null;
  mixedSkus: boolean;
}

interface UnattributedLaborResponse {
  timeZone: string;
  dayCount: number;
  rows: UnattributedLaborRow[];
  byBatchItem: UnattributedLaborByBatchItemRow[];
  byBatch: UnattributedLaborByBatchRow[];
  bySerial: Array<{
    serialFciId: number | null;
    deviceSerial: string;
    netSeconds: number;
    rowCount: number;
    lastScannedAt: string;
    resolutionPath: string;
    offBatchItemLabel: string | null;
    serialArchived: boolean;
    followedFromSerialFciId: number | null;
  }>;
  byStation: Array<{ stationKey: string; netSeconds: number; rowCount: number }>;
  byPath: Array<{ resolutionPath: string; netSeconds: number; rowCount: number }>;
  totalNetSeconds: number;
}

interface ManufacturingStationWipSkuLine {
  sku: string;
  deviceCount: number;
  displayUnits: number;
}

interface ManufacturingStationWipStationRow {
  stationId: number | null;
  stationName: string;
  isTerminal: boolean;
  deviceCount: number;
  displayUnits: number;
  skuLines: ManufacturingStationWipSkuLine[];
}

interface ManufacturingStationWipResponse {
  siteLocationId: number;
  stations: ManufacturingStationWipStationRow[];
  totalDisplayUnits: number;
}

type ReportingTab =
  | "time-tracking"
  | "station-wip"
  | "unattributed"
  | "daily-task"
  | "production-cost"
  | "clamp-qc";
type ActiveReportingTab = ReportingTab | null;

const MANUFACTURING_REPORTING_TABS = [
  { key: "daily-task" as const, label: "Daily task" },
  { key: "production-cost" as const, label: "Production cost" },
  { key: "clamp-qc" as const, label: "Clamp QC" },
  { key: "station-wip" as const, label: "Station WIP" },
  { key: "time-tracking" as const, label: "Time tracking" },
  { key: "unattributed" as const, label: "Pending labor" },
] as const;

function reportingTabFromSearch(tab: string | null): ActiveReportingTab {
  if (tab != null && MANUFACTURING_REPORTING_TABS.some((item) => item.key === tab)) {
    return tab as ReportingTab;
  }
  return null;
}

const DEFAULT_STATIONS: ManufacturingStation[] = MANUFACTURING_REPORTING_STATION_COLORS.map((station) => ({
  key: station.key,
  label: station.label,
  shortLabel: station.shortLabel,
}));

const STATION_ACCENTS = MANUFACTURING_REPORTING_STATION_ACCENTS;

function formatReportingCacheHoverHint(data: ManufacturingDailyScanResponse | undefined): string {
  if (!data?.cached_at) {
    return "Refresh time tracking from the hub cache.";
  }
  const status = data.stale ? "stale, refreshing" : "fresh";
  return `Data cached (${status}) at ${new Date(data.cached_at).toLocaleTimeString()}`;
}

function formatRangeDate(value: string): string {
  const parsed = new Date(`${value}T12:00:00`);
  if (Number.isNaN(parsed.getTime())) {
    return value;
  }
  return parsed.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

function formatDayName(value: string): string {
  const parsed = new Date(`${value}T12:00:00`);
  if (Number.isNaN(parsed.getTime())) {
    return value;
  }
  return parsed.toLocaleDateString(undefined, { weekday: "short" });
}

function formatCount(value: number): string {
  return Math.trunc(value).toLocaleString();
}

/** Total hours and minutes from net seconds (floor), for example 3:05 for three hours five minutes. */
function formatNetSecondsAsHoursMinutes(totalSeconds: number): string {
  const safe = Math.max(0, Math.floor(totalSeconds));
  const totalMinutes = Math.floor(safe / 60);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  return `${hours}:${String(minutes).padStart(2, "0")}`;
}

function formatQuantityAndNetTime(quantity: number, netSeconds: number): string {
  return `${formatCount(quantity)} / ${formatNetSecondsAsHoursMinutes(netSeconds)}`;
}

function formatNetHours(totalSeconds: number): string {
  return `${(Math.max(0, totalSeconds) / 3600).toLocaleString(undefined, {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  })} hr`;
}

function formatNullableNumber(value: number | null): string {
  return value == null ? "-" : value.toLocaleString();
}

function formatResolutionPath(value: string): string {
  return value
    .replace(/^unattributed_/, "")
    .replaceAll("_", " ")
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function formatBatchItemSkuCell(row: UnattributedLaborByBatchItemRow): ReactNode {
  if (row.skuLabel === "No scan target") {
    return (
      <HoverHintContent hoverHint="These scans stored only a device serial on the labor row (no serial_fci_id, batch item, or batch). The resolver cannot reach a work order from that scan alone, even when a serial_fci row exists for the device. Most come from Test Team / Config.">
        <span className="font-medium text-slate-900">{row.skuLabel}</span>
      </HoverHintContent>
    );
  }
  return <span className="font-medium text-slate-900">{row.skuLabel}</span>;
}

function formatBatchItemLabelCell(row: UnattributedLaborByBatchItemRow): ReactNode {
  if (row.batchItemLabel == null || row.batchItemLabel.trim() === "") {
    return <span className="text-slate-700">-</span>;
  }
  if (row.batchItemId != null) {
    return <span className="text-slate-700">{Number(row.batchItemLabel).toLocaleString()}</span>;
  }
  return <span className="font-medium text-slate-700">{row.batchItemLabel}</span>;
}

function formatBatchSampleSku(row: UnattributedLaborByBatchRow): ReactNode {
  if (row.mixedSkus) {
    return (
      <HoverHintContent hoverHint="This batch group spans more than one finished SKU. A single sample SKU would be misleading, so none is shown.">
        <span className="text-slate-700">Mixed SKUs</span>
      </HoverHintContent>
    );
  }
  return <span className="text-slate-700">{row.sampleSku ?? "-"}</span>;
}

function distinctCount<T>(rows: T[], pick: (row: T) => string | number | null | undefined): number {
  const values = new Set<string>();
  for (const row of rows) {
    const value = pick(row);
    if (value != null && String(value).trim() !== "") {
      values.add(String(value));
    }
  }
  return values.size;
}

function stationCount(row: ManufacturingDailyScanRow, stationKey: string): number {
  return Number(row.counts[stationKey] ?? 0) || 0;
}

function stationNetSeconds(row: ManufacturingDailyScanRow, stationKey: string): number {
  const byStation = row.netSeconds ?? {};
  return Number(byStation[stationKey] ?? 0) || 0;
}

function sumRowsByStation(rows: ManufacturingDailyScanRow[], stations: ManufacturingStation[]): Record<string, number> {
  const counts = Object.fromEntries(stations.map((station) => [station.key, 0]));
  for (const row of rows) {
    for (const station of stations) {
      counts[station.key] = (counts[station.key] ?? 0) + stationCount(row, station.key);
    }
  }
  return counts;
}

function sumNetSecondsByStation(
  rows: ManufacturingDailyScanRow[],
  stations: ManufacturingStation[],
): Record<string, number> {
  const netSeconds = Object.fromEntries(stations.map((station) => [station.key, 0]));
  for (const row of rows) {
    for (const station of stations) {
      netSeconds[station.key] = (netSeconds[station.key] ?? 0) + stationNetSeconds(row, station.key);
    }
  }
  return netSeconds;
}

function totalNetSecondsAcrossStations(
  netByStation: Record<string, number>,
  stations: ManufacturingStation[],
): number {
  let total = 0;
  for (const station of stations) {
    total += Number(netByStation[station.key] ?? 0) || 0;
  }
  return total;
}

function reportDayFromRows(rows: ManufacturingDailyScanRow[], stations: ManufacturingStation[]): ManufacturingReportDay | null {
  const first = rows[0];
  const last = rows[rows.length - 1];
  if (!first || !last) return null;
  if (rows.length > 1) {
    return {
      key: `${first.date}:${last.date}`,
      title: "Weekend",
      subtitle: `${formatRangeDate(first.date)} - ${formatRangeDate(last.date)}`,
      rows,
      counts: sumRowsByStation(rows, stations),
      netSeconds: sumNetSecondsByStation(rows, stations),
    };
  }
  return {
    key: first.date,
    title: formatDayName(first.date),
    subtitle: formatRangeDate(first.date),
    rows,
    counts: sumRowsByStation(rows, stations),
    netSeconds: sumNetSecondsByStation(rows, stations),
  };
}

export function ManufacturingReportingPage() {
  const queryClient = useQueryClient();
  const dailyTaskFetching =
    useIsFetching({ queryKey: ["manufacturing-reporting-daily-task"] }) > 0;
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState<ActiveReportingTab>(() =>
    reportingTabFromSearch(searchParams.get("tab")),
  );
  const [unattributedDays, setUnattributedDays] = useState(14);
  const [dailyTaskViewingToday, setDailyTaskViewingToday] = useState(false);
  const [productionCostRefreshGeneration, setProductionCostRefreshGeneration] = useState(0);
  const [clampQcRefreshGeneration, setClampQcRefreshGeneration] = useState(0);

  useEffect(() => {
    if (searchParams.has("quickbooksTime")) {
      setActiveTab("daily-task");
    }
  }, [searchParams]);
  const dailyScansQueryKey = ["manufacturing", "reporting", "daily-scans", "station-net-seconds"] as const;
  const dailyScansQuery = useQuery<ManufacturingDailyScanResponse, ApiError>({
    queryKey: dailyScansQueryKey,
    queryFn: () =>
      apiFetch<ManufacturingDailyScanResponse>("/api/manufacturing/reporting/daily-scans", {
        query: { background: "1" },
      }),
    enabled: activeTab === "time-tracking",
    staleTime: 60_000,
    refetchOnWindowFocus: false,
  });

  useHubCacheStream({
    channels: [HUB_CACHE_CHANNEL.manufacturingReportingDailyScans],
    enabled: activeTab === "time-tracking",
    onEvent: (event) => {
      if (event.phase === "ready" || event.phase === "invalidated") {
        void queryClient.invalidateQueries({ queryKey: dailyScansQueryKey });
      }
    },
  });
  const unattributedQuery = useQuery<UnattributedLaborResponse, ApiError>({
    queryKey: ["manufacturing", "reporting", "unattributed-labor", unattributedDays],
    queryFn: () =>
      apiFetch<UnattributedLaborResponse>("/api/manufacturing/reporting/unattributed-labor", {
        query: { days: unattributedDays, background: "1" },
      }),
    enabled: activeTab === "unattributed",
    staleTime: 60_000,
    refetchOnWindowFocus: false,
  });

  useHubCacheStream({
    channels: [HUB_CACHE_CHANNEL.manufacturingReportingUnattributedLabor(unattributedDays)],
    enabled: activeTab === "unattributed",
    onEvent: (event) => {
      if (event.phase === "ready" || event.phase === "invalidated") {
        void queryClient.invalidateQueries({
          queryKey: ["manufacturing", "reporting", "unattributed-labor", unattributedDays],
        });
      }
    },
  });
  const stationWipQuery = useQuery<ManufacturingStationWipResponse, ApiError>({
    queryKey: ["manufacturing", "reporting", "station-wip"],
    queryFn: () =>
      apiFetch<ManufacturingStationWipResponse>("/api/manufacturing/reporting/station-wip", {
        query: { background: "1" },
      }),
    enabled: activeTab === "station-wip",
    staleTime: 60_000,
    refetchOnWindowFocus: false,
  });

  useHubCacheStream({
    channels: [HUB_CACHE_CHANNEL.manufacturingReportingStationWip],
    enabled: activeTab === "station-wip",
    onEvent: (event) => {
      if (event.phase === "ready" || event.phase === "invalidated") {
        void queryClient.invalidateQueries({ queryKey: ["manufacturing", "reporting", "station-wip"] });
      }
    },
  });
  const productionCostFetching = useIsFetching({ queryKey: ["manufacturing", "reporting", "production-cost"] }) > 0;
  const clampQcFetching = useIsFetching({ queryKey: ["manufacturing", "reporting", "clamp-qc-rate"] }) > 0;

  const rows = dailyScansQuery.data?.rows ?? [];
  const rollingRows = dailyScansQuery.data?.rollingRows ?? rows.slice(-14);
  const stations = dailyScansQuery.data?.stations ?? DEFAULT_STATIONS;
  const unattributed = unattributedQuery.data;
  const unattributedRows = unattributed?.rows ?? [];
  const distinctDevices = useMemo(
    () => distinctCount(unattributedRows, (row) => row.serialFciId ?? row.deviceSerial),
    [unattributedRows],
  );
  const distinctSkus = useMemo(
    () => distinctCount(unattributedRows, (row) => row.batchItemSku ?? row.serialFinishedSku),
    [unattributedRows],
  );
  const distinctBatches = useMemo(() => distinctCount(unattributedRows, (row) => row.batchId), [unattributedRows]);

  const stationWipRows = useMemo(() => {
    const rows = stationWipQuery.data?.stations ?? [];
    return [...rows].sort((left, right) =>
      compareSgsProductionCalendarStationOrder(
        { stationId: left.stationId, isTerminal: left.isTerminal, deviceCount: left.deviceCount },
        { stationId: right.stationId, isTerminal: right.isTerminal, deviceCount: right.deviceCount },
      ),
    );
  }, [stationWipQuery.data?.stations]);

  const stationWipTotalDevices = useMemo(
    () => stationWipRows.reduce((sum, row) => sum + row.deviceCount, 0),
    [stationWipRows],
  );

  const summary = useMemo(() => sumRowsByStation(rollingRows, stations), [rollingRows, stations]);
  const summaryNetSeconds = useMemo(() => sumNetSecondsByStation(rollingRows, stations), [rollingRows, stations]);

  const weeklyRollups = useMemo((): WeeklyScanRollup[] => {
    const windowSize = 7;
    const rollups: WeeklyScanRollup[] = [];
    for (let index = 0; index < rows.length; index += windowSize) {
      const windowRows = rows.slice(index, index + windowSize);
      const first = windowRows[0];
      const last = windowRows[windowRows.length - 1];
      if (!first || !last) continue;
      const weekdayRows = windowRows.slice(0, 5);
      const weekendRows = windowRows.slice(5);
      const days = [
        ...weekdayRows.map((row) => reportDayFromRows([row], stations)),
        reportDayFromRows(weekendRows, stations),
      ].filter((day): day is ManufacturingReportDay => day !== null);
      rollups.push({
        label: `${formatRangeDate(first.date)} - ${formatRangeDate(last.date)}`,
        startDate: first.date,
        endDate: last.date,
        days,
        counts: sumRowsByStation(windowRows, stations),
        netSeconds: sumNetSecondsByStation(windowRows, stations),
      });
    }
    return rollups.reverse().map((rollup, index) => ({
      ...rollup,
      label: index === 0 ? `Current week: ${rollup.label}` : rollup.label,
    }));
  }, [rows, stations]);

  const refreshBusy =
    activeTab == null ||
    (activeTab === "time-tracking" && dailyScansQuery.isFetching) ||
    (activeTab === "station-wip" && stationWipQuery.isFetching) ||
    (activeTab === "unattributed" && unattributedQuery.isFetching) ||
    (activeTab === "production-cost" && productionCostFetching) ||
    (activeTab === "clamp-qc" && clampQcFetching) ||
    (activeTab === "daily-task" && dailyTaskFetching);
  const refreshSpinning =
    (activeTab === "time-tracking" && dailyScansQuery.isFetching) ||
    (activeTab === "station-wip" && stationWipQuery.isFetching) ||
    (activeTab === "unattributed" && unattributedQuery.isFetching) ||
    (activeTab === "production-cost" && productionCostFetching) ||
    (activeTab === "clamp-qc" && clampQcFetching) ||
    (activeTab === "daily-task" && dailyTaskFetching);
  const refreshButton = (
    <Button
      type="button"
      variant="secondary"
      onClick={() => {
        if (activeTab === "time-tracking") {
          void queryClient.fetchQuery({
            queryKey: dailyScansQueryKey,
            queryFn: () =>
              apiFetch<ManufacturingDailyScanResponse>("/api/manufacturing/reporting/daily-scans", {
                query: { refresh: "1" },
              }),
          });
        } else if (activeTab === "station-wip") {
          void queryClient.fetchQuery({
            queryKey: ["manufacturing", "reporting", "station-wip"],
            queryFn: () =>
              apiFetch<ManufacturingStationWipResponse>("/api/manufacturing/reporting/station-wip", {
                query: { refresh: "1" },
              }),
          });
        } else if (activeTab === "unattributed") {
          void queryClient.fetchQuery({
            queryKey: ["manufacturing", "reporting", "unattributed-labor", unattributedDays],
            queryFn: () =>
              apiFetch<UnattributedLaborResponse>("/api/manufacturing/reporting/unattributed-labor", {
                query: { days: unattributedDays, refresh: "1" },
              }),
          });
        } else if (activeTab === "production-cost") {
          setProductionCostRefreshGeneration((current) => current + 1);
        } else if (activeTab === "clamp-qc") {
          setClampQcRefreshGeneration((current) => current + 1);
        } else {
          void queryClient.refetchQueries({ queryKey: ["manufacturing-reporting-daily-task"] });
        }
      }}
      disabled={refreshBusy}
    >
      <RefreshCw className={cn("mr-2 h-4 w-4", refreshSpinning ? "animate-spin" : "")} aria-hidden />
      Refresh
    </Button>
  );

  return (
    <div className="space-y-6">
      <header className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <div className="flex flex-wrap items-center gap-2">
            <BarChart3 className="h-6 w-6 text-brand-600" aria-hidden />
            <PageTitleWithHelp
              titleClassName="text-xl"
              hoverHint="Time tracking shows manufacturing station labor by day from hub device sessions and from the mobile labor app (Receiving, Transfers, and Misc via public.labor_sessions — Misc rolls up the mobile Orders task type). Counts on Receiving are inbound receipt lines, Transfers are destination receive lines, and Transfer Pack are packed transfer-order lines (not unit quantity). Misc counts ended clock-in sessions. Other stations use deduplicated device scans. Config, Cal+Test, Pre-Encapsulation, Final QC, and Pre-Pack omit unserialized Accessory SKUs (fiber cables, magnets, and similar) from the quantity and from clocked time when a session packed only those accessories. Hover a station line for the SKU or session breakdown. Net clocked time on hub stations includes only ended sessions that recorded at least one counted scan or packed quantity (empty clock-in/clock-out sessions are omitted). Station WIP shows live SGS-only device counts at each floor station from serial_fci.station_id. Unattributed labor lists scans that still show in time tracking but cannot currently resolve to a work order. Clamp QC shows clamps checked per productive labor hour by operator."
            >
              Manufacturing reporting
            </PageTitleWithHelp>
          </div>
        </div>
        {activeTab === "daily-task" && dailyTaskViewingToday ? (
          <HoverHintTrigger trigger={refreshButton} align="center">
            Today&apos;s report refreshes automatically every{" "}
            {Math.round(DAILY_TASK_REPORT_AUTO_REFRESH_MS / 1000)} seconds. Click for an immediate update.
          </HoverHintTrigger>
        ) : activeTab === "time-tracking" ? (
          <HoverHintTrigger trigger={refreshButton} align="center">
            {formatReportingCacheHoverHint(dailyScansQuery.data)}
          </HoverHintTrigger>
        ) : (
          refreshButton
        )}
      </header>

      <div className="flex flex-wrap gap-2 border-b border-slate-200">
        {MANUFACTURING_REPORTING_TABS.map((tab) => (
          <button
            key={tab.key}
            type="button"
            className={cn(
              "-mb-px rounded-t-md border border-transparent px-3 py-2 text-sm font-medium",
              activeTab === tab.key
                ? "border-slate-200 border-b-white bg-white text-slate-900"
                : "text-slate-600 hover:bg-slate-50 hover:text-slate-900",
            )}
            onClick={() => setActiveTab(tab.key)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {activeTab == null ? (
        <p className="rounded-lg border border-dashed border-slate-200 bg-slate-50 px-4 py-10 text-center text-sm text-slate-600">
          Select a report tab above to load data.
        </p>
      ) : activeTab === "time-tracking" ? (
        <>
      <div className="overflow-x-auto pb-0.5 [-webkit-overflow-scrolling:touch]">
        <div className="grid min-w-[720px] grid-cols-2 gap-2 sm:grid-cols-4 xl:grid-cols-6 2xl:grid-cols-11 md:min-w-0">
          {stations.map((station, index) => (
            <Card key={station.key} className="min-w-0">
              <CardBody className="flex flex-col gap-1 px-2 py-3 sm:px-3 sm:py-4">
                <TimeTrackingStationHoverHint
                  stationLabel={station.label}
                  stationKey={station.key}
                  quantity={summary[station.key] ?? 0}
                  netSeconds={summaryNetSeconds[station.key] ?? 0}
                  excludedNetSeconds={rollingRows.reduce(
                    (sum, row) => sum + (Number(row.excludedNetSeconds?.[station.key] ?? 0) || 0),
                    0,
                  )}
                  lines={rollingRows.flatMap((row) => row.breakdowns?.[station.key] ?? [])}
                >
                  <span className="flex items-center gap-1.5 text-[10px] font-medium uppercase tracking-wide text-slate-500 sm:text-xs">
                    <span
                      className={cn("h-2 w-2 shrink-0 rounded-full sm:h-2.5 sm:w-2.5", STATION_ACCENTS[index % STATION_ACCENTS.length])}
                    />
                    <span className="truncate">{station.shortLabel}</span>
                  </span>
                  <span className="text-base font-semibold tabular-nums leading-tight text-slate-900 sm:text-xl">
                    {formatQuantityAndNetTime(summary[station.key] ?? 0, summaryNetSeconds[station.key] ?? 0)}
                  </span>
                </TimeTrackingStationHoverHint>
              </CardBody>
            </Card>
          ))}
        </div>
      </div>

      <div className="flex flex-col gap-6">
        <h2 className="text-base font-semibold text-slate-900">Four-week daily report</h2>
        {dailyScansQuery.isLoading ? (
          <p className="py-6 text-center text-sm text-slate-500">Loading manufacturing scans...</p>
        ) : dailyScansQuery.error ? (
          <p className="py-6 text-center text-sm text-rose-700">{dailyScansQuery.error.message}</p>
        ) : rows.length === 0 ? (
          <p className="py-6 text-center text-sm text-slate-600">
            No manufacturing labor scans were found for the report window.
          </p>
        ) : (
          <div className="flex flex-col gap-8">
            {weeklyRollups.map((rollup) => (
              <div key={`${rollup.startDate}:${rollup.endDate}`} className="flex flex-col gap-2">
                <h3 className="text-sm font-semibold text-slate-900">{rollup.label}</h3>
                <div className="overflow-x-auto pb-0.5 [-webkit-overflow-scrolling:touch]">
                  <div className="grid min-w-[720px] grid-cols-7 gap-2 sm:min-w-0">
                    {rollup.days.map((day) => (
                      <div
                        key={day.key}
                        className="flex min-w-0 flex-1 flex-col overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm"
                      >
                        <div className="border-b border-slate-100 px-2 py-1">
                          <div className="flex items-start justify-between gap-2">
                            <div className="min-w-0 text-left">
                              <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                                {day.title}
                              </div>
                              <div className="text-xs text-slate-500">{day.subtitle}</div>
                            </div>
                            <div className="shrink-0 text-right">
                              <div className="text-[10px] font-medium uppercase tracking-wide text-slate-400">
                                Total
                              </div>
                              <div className="text-xs font-semibold tabular-nums text-slate-800">
                                {formatNetSecondsAsHoursMinutes(
                                  totalNetSecondsAcrossStations(day.netSeconds, stations),
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                        <div className="divide-y divide-slate-100">
                          {stations.map((station, index) => (
                            <TimeTrackingStationHoverHint
                              key={station.key}
                              layout="row"
                              stationLabel={station.label}
                              stationKey={station.key}
                              quantity={day.counts[station.key] ?? 0}
                              netSeconds={day.netSeconds[station.key] ?? 0}
                              excludedNetSeconds={day.rows.reduce(
                                (sum, row) => sum + (Number(row.excludedNetSeconds?.[station.key] ?? 0) || 0),
                                0,
                              )}
                              lines={day.rows.flatMap((row) => row.breakdowns?.[station.key] ?? [])}
                            >
                              <div className="flex min-h-7 items-center justify-between gap-2 px-2 py-0.5">
                              <span className="flex min-w-0 items-center gap-1.5 text-[11px] font-medium text-slate-600">
                                <span
                                  className={cn(
                                    "h-2 w-2 shrink-0 rounded-full",
                                    STATION_ACCENTS[index % STATION_ACCENTS.length],
                                  )}
                                />
                                <span className="truncate">{station.shortLabel}</span>
                              </span>
                              <span className="font-semibold tabular-nums text-slate-900">
                                {formatQuantityAndNetTime(
                                  day.counts[station.key] ?? 0,
                                  day.netSeconds[station.key] ?? 0,
                                )}
                              </span>
                              </div>
                            </TimeTrackingStationHoverHint>
                          ))}
                        </div>
                      </div>
                    ))}
                    <div className="min-w-0 overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm">
                      <div className="border-b border-slate-100 px-2 py-1">
                        <div className="flex items-start justify-between gap-2">
                          <div className="min-w-0 text-left">
                            <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">Week rollup</div>
                            <div className="text-xs text-slate-500">{rollup.label.replace(/^Current week: /, "")}</div>
                          </div>
                          <div className="shrink-0 text-right">
                            <div className="text-[10px] font-medium uppercase tracking-wide text-slate-400">Total</div>
                            <div className="text-xs font-semibold tabular-nums text-slate-800">
                              {formatNetSecondsAsHoursMinutes(
                                totalNetSecondsAcrossStations(rollup.netSeconds, stations),
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                      <dl className="divide-y divide-slate-100">
                        {stations.map((station, index) => (
                          <TimeTrackingStationHoverHint
                            key={station.key}
                            layout="row"
                            stationLabel={station.label}
                            stationKey={station.key}
                            quantity={rollup.counts[station.key] ?? 0}
                            netSeconds={rollup.netSeconds[station.key] ?? 0}
                            excludedNetSeconds={rollup.days.reduce(
                              (sum, day) =>
                                sum +
                                day.rows.reduce(
                                  (daySum, row) => daySum + (Number(row.excludedNetSeconds?.[station.key] ?? 0) || 0),
                                  0,
                                ),
                              0,
                            )}
                            lines={rollup.days.flatMap((day) =>
                              day.rows.flatMap((row) => row.breakdowns?.[station.key] ?? []),
                            )}
                          >
                            <div className="flex min-h-7 items-center justify-between gap-2 px-2 py-0.5">
                            <dt className="flex min-w-0 items-center gap-1.5 text-[11px] font-medium text-slate-600">
                              <span
                                className={cn(
                                  "h-2 w-2 shrink-0 rounded-full",
                                  STATION_ACCENTS[index % STATION_ACCENTS.length],
                                )}
                              />
                              <span className="truncate">{station.shortLabel}</span>
                            </dt>
                            <dd className="font-semibold tabular-nums text-slate-900">
                              {formatQuantityAndNetTime(
                                rollup.counts[station.key] ?? 0,
                                rollup.netSeconds[station.key] ?? 0,
                              )}
                            </dd>
                            </div>
                          </TimeTrackingStationHoverHint>
                        ))}
                      </dl>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-4 text-xs text-slate-500">
        {stations.map((station, index) => (
          <span key={station.key} className="inline-flex items-center gap-1">
            <span className={cn("h-2.5 w-2.5 rounded-full", STATION_ACCENTS[index % STATION_ACCENTS.length])} />
            {station.label}
          </span>
        ))}
      </div>
        </>
      ) : activeTab === "station-wip" ? (
        <div className="space-y-6">
          <div className="flex flex-wrap items-center gap-2">
            <h2 className="text-base font-semibold text-slate-900">Floor station WIP</h2>
            <HoverHintWithGlyph hoverHint="Live device counts at each Smart Grid Solutions floor station. Includes serial_fci rows at SGS location or on an SGS batch (excluding Shipped). Units with a scrap QC incident or serial_fci.scrap are omitted (including Root Cause Analysis holds already dispositioned as scrap). SDTestApp Cal+Test advances calibrated units from Configuration to Cal+Test (station id 6). Non-serialized bulk on open SGS batch shipments counts toward Quality Control or Pack out." />
          </div>

          {stationWipQuery.isLoading ? (
            <p className="py-6 text-center text-sm text-slate-500">Loading station WIP...</p>
          ) : stationWipQuery.error ? (
            <p className="py-6 text-center text-sm text-rose-700">{stationWipQuery.error.message}</p>
          ) : stationWipRows.length === 0 ? (
            <p className="rounded-lg border border-slate-200 bg-white px-4 py-8 text-center text-sm text-slate-600">
              No units are currently attributed to an SGS floor station.
            </p>
          ) : (
            <>
              <Card>
                <CardBody className="flex flex-col gap-1 px-3 py-4 sm:px-4">
                  <span className="text-xs font-medium uppercase tracking-wide text-slate-500">
                    Total devices on floor
                  </span>
                  <span className="text-2xl font-semibold tabular-nums text-slate-900">
                    {formatCount(stationWipTotalDevices)}
                  </span>
                </CardBody>
              </Card>

              <div className="overflow-x-auto pb-0.5 [-webkit-overflow-scrolling:touch]">
                <div className="grid min-w-[720px] grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 md:min-w-0">
                  {stationWipRows.map((row, index) => (
                    <Card key={row.stationId ?? "unassigned"} className="min-w-0">
                      <CardBody className="flex flex-col gap-1 px-2 py-3 sm:px-3 sm:py-4">
                        <span className="flex items-center gap-1.5 text-[10px] font-medium uppercase tracking-wide text-slate-500 sm:text-xs">
                          <span
                            className={cn(
                              "h-2 w-2 shrink-0 rounded-full sm:h-2.5 sm:w-2.5",
                              STATION_ACCENTS[index % STATION_ACCENTS.length],
                            )}
                          />
                          <span className="truncate">{row.stationName}</span>
                        </span>
                        <HoverHintTrigger
                          align="start"
                          trigger={
                            <span className="cursor-help text-base font-semibold tabular-nums leading-tight text-slate-900 sm:text-xl">
                              {formatCount(row.deviceCount)}
                            </span>
                          }
                        >
                          <div className="mb-1 font-medium text-slate-800">{row.stationName}</div>
                          {row.skuLines.length === 0 ? (
                            <span className="text-slate-500">No line-level detail.</span>
                          ) : (
                            <div className="space-y-0.5">
                              {row.skuLines.map((line) => (
                                <div key={line.sku} className="tabular-nums">
                                  <span className="font-semibold text-slate-900">
                                    {formatCount(line.deviceCount)}
                                  </span>
                                  <span className="text-slate-600"> {line.sku}</span>
                                </div>
                              ))}
                            </div>
                          )}
                        </HoverHintTrigger>
                      </CardBody>
                    </Card>
                  ))}
                </div>
              </div>
            </>
          )}
        </div>
      ) : activeTab === "unattributed" ? (
        <div className="space-y-6">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-center gap-2">
              <h2 className="text-base font-semibold text-slate-900">Pending labor</h2>
              <HoverHintWithGlyph hoverHint="Pending means the scan is not on a work order yet. Alternate labor stays here until the unit is moved onto a batch or rebuilt; it then follows the new episode (even if that makes the unit look like roughly twice a normal build). Consumed labor already has a work order through the batch line and does not appear here. Archived-episode rows are leftover scans on a superseded serial_fci that have not yet followed." />
            </div>
            <label className="flex items-center gap-2 text-sm text-slate-700">
              Window
              <select
                value={unattributedDays}
                onChange={(event) => setUnattributedDays(Number(event.target.value))}
                className="h-10 rounded-md border border-slate-300 bg-white px-3 text-sm text-slate-900 shadow-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-200"
              >
                {[7, 14, 30, 90].map((days) => (
                  <option key={days} value={days}>
                    {days} days
                  </option>
                ))}
              </select>
            </label>
          </div>

          {unattributedQuery.isLoading ? (
            <p className="py-6 text-center text-sm text-slate-500">Loading pending labor...</p>
          ) : unattributedQuery.error ? (
            <p className="py-6 text-center text-sm text-rose-700">{unattributedQuery.error.message}</p>
          ) : unattributedRows.length === 0 ? (
            <p className="rounded-lg border border-slate-200 bg-white px-4 py-8 text-center text-sm text-slate-600">
              No pending labor was found for this window.
            </p>
          ) : (
            <>
              <div className="grid gap-2 sm:grid-cols-2 xl:grid-cols-4">
                {[
                  { label: "Pending time", value: formatNetHours(unattributed?.totalNetSeconds ?? 0) },
                  { label: "Distinct devices", value: distinctDevices.toLocaleString() },
                  { label: "Distinct SKUs", value: distinctSkus.toLocaleString() },
                  { label: "Distinct batches", value: distinctBatches.toLocaleString() },
                ].map((item) => (
                  <Card key={item.label}>
                    <CardBody className="flex flex-col gap-1 px-3 py-4">
                      <span className="text-xs font-medium uppercase tracking-wide text-slate-500">{item.label}</span>
                      <span className="text-xl font-semibold tabular-nums text-slate-900">{item.value}</span>
                    </CardBody>
                  </Card>
                ))}
              </div>

              <div className="grid gap-6 xl:grid-cols-[minmax(0,1.4fr)_minmax(0,1fr)]">
                <Card>
                  <CardBody className="space-y-3 p-4">
                    <div className="flex items-center gap-2">
                      <h3 className="text-sm font-semibold text-slate-900">By SKU / batch item</h3>
                      <HoverHintWithGlyph hoverHint="Groups labor by batch item when the scan recorded one. When there is no batch item, off-batch pool labels show why the serial is not on a production line: alternate, qc-repair, qc-scrap, or qc-in progress (from reason_not_in_batch and the latest quality incident). No scan target means the labor row has only a device serial." />
                    </div>
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-slate-200 text-sm">
                        <thead className="text-left text-xs uppercase tracking-wide text-slate-500">
                          <tr>
                            <th className="px-2 py-2">SKU</th>
                            <th className="px-2 py-2">Batch</th>
                            <th className="px-2 py-2">Batch item</th>
                            <th className="px-2 py-2 text-right">Rows</th>
                            <th className="px-2 py-2 text-right">Time</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                          {(unattributed?.byBatchItem ?? []).slice(0, 25).map((row) => (
                            <tr key={row.groupKey}>
                              <td className="px-2 py-2">{formatBatchItemSkuCell(row)}</td>
                              <td className="px-2 py-2 text-slate-700">{formatNullableNumber(row.batchId)}</td>
                              <td className="px-2 py-2">{formatBatchItemLabelCell(row)}</td>
                              <td className="px-2 py-2 text-right tabular-nums text-slate-700">{row.rowCount}</td>
                              <td className="px-2 py-2 text-right tabular-nums text-slate-900">{formatNetHours(row.netSeconds)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </CardBody>
                </Card>

                <Card>
                  <CardBody className="space-y-3 p-4">
                    <h3 className="text-sm font-semibold text-slate-900">Why pending</h3>
                    <div className="space-y-2">
                      {(unattributed?.byPath ?? []).map((row) => (
                        <div key={row.resolutionPath} className="flex items-center justify-between gap-3 rounded-md bg-slate-50 px-3 py-2 text-sm">
                          <span className="font-medium text-slate-700">{formatResolutionPath(row.resolutionPath)}</span>
                          <span className="tabular-nums text-slate-900">{formatNetHours(row.netSeconds)}</span>
                        </div>
                      ))}
                    </div>
                  </CardBody>
                </Card>
              </div>

              <div className="grid gap-6 xl:grid-cols-2">
                <Card>
                  <CardBody className="space-y-3 p-4">
                    <div className="flex items-center gap-2">
                      <h3 className="text-sm font-semibold text-slate-900">By batch</h3>
                      <HoverHintWithGlyph hoverHint="Sample SKU appears only when every row in the batch group shares one finished SKU. Mixed SKUs means the group spans more than one finished good." />
                    </div>
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-slate-200 text-sm">
                        <thead className="text-left text-xs uppercase tracking-wide text-slate-500">
                          <tr>
                            <th className="px-2 py-2">Batch</th>
                            <th className="px-2 py-2">Sample SKU</th>
                            <th className="px-2 py-2 text-right">Rows</th>
                            <th className="px-2 py-2 text-right">Time</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                          {(unattributed?.byBatch ?? []).slice(0, 25).map((row) => (
                            <tr key={row.batchId ?? "none"}>
                              <td className="px-2 py-2 font-medium text-slate-900">{formatNullableNumber(row.batchId)}</td>
                              <td className="px-2 py-2">{formatBatchSampleSku(row)}</td>
                              <td className="px-2 py-2 text-right tabular-nums text-slate-700">{row.rowCount}</td>
                              <td className="px-2 py-2 text-right tabular-nums text-slate-900">{formatNetHours(row.netSeconds)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </CardBody>
                </Card>

                <Card>
                  <CardBody className="space-y-3 p-4">
                    <h3 className="text-sm font-semibold text-slate-900">By serial</h3>
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-slate-200 text-sm">
                        <thead className="text-left text-xs uppercase tracking-wide text-slate-500">
                          <tr>
                            <th className="px-2 py-2">Device</th>
                            <th className="px-2 py-2">Serial FCI</th>
                            <th className="px-2 py-2">Pool</th>
                            <th className="px-2 py-2">Why</th>
                            <th className="px-2 py-2 text-right">Time</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                          {(unattributed?.bySerial ?? []).slice(0, 25).map((row) => (
                            <tr key={`${row.serialFciId ?? "none"}:${row.deviceSerial}`}>
                              <td className="px-2 py-2 font-medium text-slate-900">{row.deviceSerial}</td>
                              <td className="px-2 py-2 text-slate-700">{formatNullableNumber(row.serialFciId)}</td>
                              <td className="px-2 py-2 text-slate-700">{row.offBatchItemLabel ?? (row.serialArchived ? "archived-episode" : "—")}</td>
                              <td className="px-2 py-2 text-slate-700">{formatResolutionPath(row.resolutionPath)}</td>
                              <td className="px-2 py-2 text-right tabular-nums text-slate-900">{formatNetHours(row.netSeconds)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </CardBody>
                </Card>
              </div>
            </>
          )}
        </div>
      ) : activeTab === "daily-task" ? (
        <DailyTaskReportTab onViewingTodayChange={setDailyTaskViewingToday} />
      ) : activeTab === "production-cost" ? (
        <ProductionCostReportTab refreshGeneration={productionCostRefreshGeneration} />
      ) : activeTab === "clamp-qc" ? (
        <ClampQcReportTab refreshGeneration={clampQcRefreshGeneration} />
      ) : null}
    </div>
  );
}
